import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, CircularProgress, Alert, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton, Divider, Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { API_BASE } from '../api';
import { useAuth } from '../contexts/AuthContext';

function b64urlToBuffer(b64url) {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0)).buffer;
}
function bufferToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}
function serializeCredential(cred) {
  if (typeof cred.toJSON === 'function') return cred.toJSON();
  const r = cred.response;
  return {
    id: cred.id,
    rawId: bufferToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON:    bufferToB64url(r.clientDataJSON),
      attestationObject: bufferToB64url(r.attestationObject),
    },
  };
}
function prepareCreateOptions(opts) {
  const o = {
    ...opts,
    challenge: b64urlToBuffer(opts.challenge),
    user: { ...opts.user, id: b64urlToBuffer(opts.user.id) },
  };
  if (o.excludeCredentials) {
    o.excludeCredentials = o.excludeCredentials.map((c) => ({ ...c, id: b64urlToBuffer(c.id) }));
  }
  return o;
}

export default function FaceIdSetup({ open, onClose }) {
  const { token } = useAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');

  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadCredentials = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/webauthn/credentials`, { headers: authHeaders });
      const data = await res.json();
      setCredentials(data.credentials || []);
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (open) { setError(''); setSuccess(''); loadCredentials(); }
  }, [open, loadCredentials]);

  const handleRegister = async () => {
    setError(''); setSuccess('');
    if (!window.PublicKeyCredential) {
      return setError('Este navegador não suporta autenticação biométrica.');
    }
    setRegistering(true);
    try {
      const optRes = await fetch(`${API_BASE}/auth/webauthn/register-options`, {
        method: 'POST', headers: authHeaders,
      });
      const opts = await optRes.json();
      if (!optRes.ok) throw new Error(opts.error || 'Erro ao iniciar registro.');

      const credential = await navigator.credentials.create({ publicKey: prepareCreateOptions(opts) });
      if (!credential) throw new Error('Registro cancelado.');

      const regRes = await fetch(`${API_BASE}/auth/webauthn/register`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ name: 'Face ID', credential: serializeCredential(credential) }),
      });
      const data = await regRes.json();
      if (!regRes.ok) throw new Error(data.error || 'Falha no registro.');

      setSuccess('Face ID registrado com sucesso! Use-o no próximo login.');
      loadCredentials();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Permissão negada ou tempo esgotado. Tente novamente.');
      } else {
        setError(err.message);
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleRemove = async (credId) => {
    setError(''); setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/auth/webauthn/credentials/${encodeURIComponent(credId)}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (!res.ok) throw new Error('Erro ao remover.');
      setSuccess('Credencial removida.');
      loadCredentials();
    } catch (err) {
      setError(err.message);
    }
  };

  const supported = !!window.PublicKeyCredential;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
        <FingerprintIcon sx={{ color: '#E8770A' }} />
        Configurar Face ID / Biometria
      </DialogTitle>

      <DialogContent>
        {!supported && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Seu navegador ou dispositivo não suporta autenticação biométrica (WebAuthn).
            Use Chrome, Safari ou Edge recentes em um dispositivo com sensor biométrico.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Registre seu Face ID (iOS/Mac) ou impressão digital (Android/Windows Hello) para
          acessar o sistema sem precisar de código por e-mail nas próximas entradas.
        </Typography>

        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Button
          variant="contained"
          startIcon={registering ? <CircularProgress size={16} color="inherit" /> : <FingerprintIcon />}
          disabled={!supported || registering || loading}
          onClick={handleRegister}
          sx={{ mb: 3, bgcolor: '#2b2b2b', '&:hover': { bgcolor: '#444' } }}
          fullWidth
        >
          {registering ? 'Aguardando biometria…' : 'Registrar Face ID / Biometria'}
        </Button>

        {credentials.length > 0 && (
          <>
            <Divider sx={{ mb: 1 }}>
              <Chip label="Credenciais registradas" size="small" />
            </Divider>
            {loading ? <CircularProgress size={24} /> : (
              <List dense>
                {credentials.map((c) => (
                  <ListItem key={c.id} sx={{ border: '1px solid #eee', borderRadius: 1, mb: 0.5 }}>
                    <ListItemText
                      primary={<><FingerprintIcon sx={{ fontSize:16, mr:.5, verticalAlign:'middle', color:'#E8770A' }} />{c.name}</>}
                      secondary={`ID: ${c.id.slice(0, 16)}…`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" size="small" color="error" onClick={() => handleRemove(c.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
